import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp, NativeStackScreenProps } from '@react-navigation/native-stack';
import { deleteDoc, doc } from 'firebase/firestore';
import { Colors, Radius, Shadow, Typography } from '../theme';
import type { OrganizerEventTemplate, RootStackParamList } from '../types';
import { firestore, readOrganizerEventTemplates } from '../firebase';
import TonyteBackground from '../components/TonyteBackground';
import { useI18n } from '../i18n';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';

type Props = NativeStackScreenProps<RootStackParamList, 'MyTemplates'>;

const MyTemplatesScreen: React.FC<Props> = ({ route }) => {
  const { userId } = route.params;
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const isIOS = Platform.OS === 'ios';
  const scrollBottomPadding = insets.bottom + (isIOS ? 180 : 80);
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const [templates, setTemplates] = useState<OrganizerEventTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await readOrganizerEventTemplates(userId);
      setTemplates(data);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { void load(); }, [load]);

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  const filtered = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(t =>
      [t.eventTitle, t.templateName, t.clubName, t.city, t.address, t.tags.join(' '), t.category]
        .some(field => field?.toLowerCase().includes(q)),
    );
  })();

  const handleUseTemplate = (template: OrganizerEventTemplate) => {
    navigation.navigate('UploadEvent', { template });
  };

  const handleDelete = (template: OrganizerEventTemplate) => {
    Alert.alert(
      t('library.deleteTemplate'),
      t('library.deleteConfirm').replace('{title}', template.eventTitle || template.templateName),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            setDeletingId(template.id);
            try {
              await deleteDoc(doc(firestore, 'organizerLibraries', userId, 'eventTemplates', template.id));
              setTemplates(prev => prev.filter(t => t.id !== template.id));
            } catch {
              Alert.alert(t('common.error'), t('library.deleteTemplateFailed'));
            } finally {
              setDeletingId(null);
            }
          },
        },
      ],
    );
  };

  return (
    <TonyteBackground appearanceAware style={[styles.container, appearance.container]}>
      {/* Header */}
      <View style={[styles.header, { paddingTop: insets.top + 12 }]}>
        <TouchableOpacity style={[styles.backButton, appearance.control]} onPress={() => navigation.goBack()} activeOpacity={0.78}>
          <Icon name="arrow-left" size={20} color={theme.colors.icon} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={styles.headerKicker}>{t('library.organizerLibrary')}</Text>
          <Text style={[styles.headerTitle, appearance.textPrimary]}>{t('library.savedTemplates')}</Text>
        </View>
        <View style={styles.headerCount}>
          <Text style={styles.headerCountText}>{loading ? '…' : templates.length}</Text>
        </View>
      </View>

      {/* Search bar */}
      <View style={[styles.searchWrap, appearance.input]}>
        <Icon name="search" size={16} color={theme.colors.iconMuted} style={styles.searchIcon} />
        <TextInput
          style={[styles.searchInput, appearance.textPrimary]}
          value={search}
          onChangeText={setSearch}
          placeholder={t('library.search')}
          placeholderTextColor={theme.colors.textMuted}
          returnKeyType="search"
          clearButtonMode="while-editing"
        />
        {search.length > 0 ? (
          <TouchableOpacity onPress={() => setSearch('')} style={styles.searchClear}>
            <Icon name="x" size={14} color={theme.colors.iconMuted} />
          </TouchableOpacity>
        ) : null}
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.scrollContent, { paddingBottom: scrollBottomPadding }]}
        contentInsetAdjustmentBehavior="never"
        automaticallyAdjustContentInsets={false}
        scrollIndicatorInsets={{ bottom: scrollBottomPadding }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {loading ? (
          <View style={styles.emptyWrap}>
            <Text style={[styles.emptyText, appearance.textMuted]}>{t('library.loadingTemplates')}</Text>
          </View>
        ) : filtered.length === 0 ? (
          <View style={styles.emptyWrap}>
            <View style={styles.emptyIcon}>
              <Icon name="copy" size={28} color={Colors.sunset} />
            </View>
            <Text style={[styles.emptyTitle, appearance.textPrimary]}>
              {search.trim() ? t('library.noMatches') : t('library.noTemplates')}
            </Text>
            <Text style={[styles.emptyText, appearance.textMuted]}>
              {search.trim()
                ? t('library.searchAgain')
                : t('library.noTemplatesSub')}
            </Text>
          </View>
        ) : (
          filtered.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              deleting={deletingId === template.id}
              onUse={() => handleUseTemplate(template)}
              onDelete={() => handleDelete(template)}
            />
          ))
        )}
      </ScrollView>
    </TonyteBackground>
  );
};

const TemplateCard: React.FC<{
  template: OrganizerEventTemplate;
  deleting: boolean;
  onUse: () => void;
  onDelete: () => void;
}> = ({ template, deleting, onUse, onDelete }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  return (
  <View style={[styles.card, appearance.card]}>
    <LinearGradient
      pointerEvents="none"
      colors={theme.isDark
        ? ['rgba(255,255,255,0.1)', 'rgba(255,255,255,0.04)']
        : ['rgba(255,255,255,0.76)', 'rgba(247,238,225,0.42)']}
      style={StyleSheet.absoluteFill}
    />
    <View style={styles.cardTop}>
      {template.coverImageUrl ? (
        <Image source={{ uri: template.coverImageUrl }} style={styles.cardThumb} resizeMode="cover" />
      ) : (
        <View style={[styles.cardThumb, styles.cardThumbEmpty, appearance.control]}>
          <Icon name="image" size={22} color={theme.colors.iconFaint} />
        </View>
      )}
      <View style={styles.cardInfo}>
        <View style={styles.cardTypeBadge}>
          <Text style={styles.cardTypeText}>{t('library.eventTemplate')}</Text>
        </View>
        <Text style={[styles.cardTitle, appearance.textPrimary]} numberOfLines={2}>
          {template.eventTitle || template.templateName || t('library.untitled')}
        </Text>
        <Text style={[styles.cardVenue, appearance.textSecondary]} numberOfLines={1}>
          {template.clubName || template.address || t('library.noVenue')}
        </Text>
        {(template.city || template.defaultStartTime) ? (
          <Text style={[styles.cardMeta, appearance.textMuted]} numberOfLines={1}>
            {[
              template.city,
              template.defaultStartTime
                ? `${template.defaultStartTime}${template.defaultEndTime ? ` – ${template.defaultEndTime}` : ''}`
                : null,
            ].filter(Boolean).join(' · ')}
          </Text>
        ) : null}
        {template.tags.length > 0 ? (
          <View style={styles.tagRow}>
            {template.tags.slice(0, 4).map(tag => (
              <View key={tag} style={[styles.tag, appearance.control]}>
                <Text style={[styles.tagText, appearance.textMuted]}>{tag.startsWith('#') ? tag : `#${tag}`}</Text>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    </View>

    <View style={styles.cardActions}>
      <TouchableOpacity
        style={[styles.btnUse, deleting && styles.btnDisabled]}
        onPress={onUse}
        activeOpacity={0.82}
        disabled={deleting}
      >
        <Icon name="copy" size={14} color="#fff" />
        <Text style={styles.btnUseText}>{t('library.useTemplate')}</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.btnDelete, deleting && styles.btnDisabled]}
        onPress={onDelete}
        activeOpacity={0.82}
        disabled={deleting}
      >
        <Icon name="trash-2" size={14} color={Colors.sunset} />
      </TouchableOpacity>
    </View>
  </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: 'transparent', overflow: 'visible' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingBottom: 16,
    gap: 12,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.07)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerCenter: { flex: 1 },
  headerKicker: {
    color: Colors.sunset,
    fontSize: 9,
    fontWeight: Typography.extrabold,
    letterSpacing: 2.2,
    textTransform: 'uppercase',
  },
  headerTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.xl,
    fontWeight: Typography.bold,
    marginTop: 2,
  },
  headerCount: {
    minWidth: 36,
    height: 36,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(242,125,38,0.3)',
    backgroundColor: 'rgba(242,125,38,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
  },
  headerCountText: {
    color: Colors.sunset,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 20,
    marginBottom: 16,
    height: 46,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(255,255,255,0.055)',
    paddingHorizontal: 14,
    gap: 10,
  },
  searchIcon: {},
  searchInput: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    paddingVertical: 0,
  },
  searchClear: { padding: 4 },
  scroll: { flex: 1, backgroundColor: 'transparent', overflow: 'visible' },
  scrollContent: {
    width: '100%',
    alignSelf: 'stretch',
    backgroundColor: 'transparent',
    overflow: 'visible',
    paddingHorizontal: 20,
    gap: 12,
  },
  emptyWrap: {
    paddingTop: 60,
    alignItems: 'center',
    gap: 12,
  },
  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: 22,
    backgroundColor: 'rgba(242,125,38,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.md,
    fontWeight: Typography.bold,
    textAlign: 'center',
  },
  emptyText: {
    color: Colors.textMuted,
    fontSize: Typography.sm,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 280,
  },
  card: {
    width: '100%',
    alignSelf: 'stretch',
    maxWidth: '100%',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(16,23,34,0.72)',
    overflow: 'hidden',
    padding: 16,
    gap: 14,
    ...Shadow.card,
  },
  cardTop: {
    flexDirection: 'row',
    gap: 14,
    width: '100%',
    minWidth: 0,
  },
  cardThumb: {
    width: 72,
    height: 72,
    borderRadius: 16,
    overflow: 'hidden',
    flexShrink: 0,
  },
  cardThumbEmpty: {
    backgroundColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardInfo: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  cardTypeBadge: {
    alignSelf: 'flex-start',
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(242,125,38,0.14)',
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  cardTypeText: {
    color: Colors.sunset,
    fontSize: 9,
    fontWeight: Typography.extrabold,
    letterSpacing: 1.6,
  },
  cardTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.bold,
    lineHeight: 20,
  },
  cardVenue: {
    color: Colors.textSecondary,
    fontSize: Typography.sm,
  },
  cardMeta: {
    color: Colors.textMuted,
    fontSize: Typography.xs,
  },
  tagRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 2,
  },
  tag: {
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(255,255,255,0.07)',
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  tagText: {
    color: Colors.textMuted,
    fontSize: 10,
    fontWeight: Typography.medium,
  },
  cardActions: {
    flexDirection: 'row',
    gap: 10,
    width: '100%',
    minWidth: 0,
  },
  btnUse: {
    flex: 1,
    minWidth: 0,
    minHeight: 42,
    borderRadius: 14,
    backgroundColor: Colors.sunset,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    ...Shadow.card,
  },
  btnUseText: {
    color: '#fff',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
    flexShrink: 1,
  },
  btnDelete: {
    width: 42,
    height: 42,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(242,125,38,0.28)',
    backgroundColor: 'rgba(242,125,38,0.07)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnDisabled: {
    opacity: 0.45,
  },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  input: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.borderStrong,
    ...theme.shadows.card,
  }),
  control: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.borderStrong,
  }),
  card: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.cardBorder,
    ...theme.shadows.card,
  }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
});

export default MyTemplatesScreen;
